import React from "react";
import {Checkbox, FormControlLabel, MenuItem, TextField, Typography} from "@mui/material";
import {Grey2, Grey6, Grey7, Grey17} from "../../theme/styles";
import {Condition, DisplayItem, FormDefinition, OPERATORS_TAKING_A_VALUE, Placement, Section,
        FILTERABLE_CONTROLS, RANGE_FACET_CONTROLS, Selection, ShapeField, SHOW_WHEN_OPERATORS, Tab,
        WIDTHS, OwnDefinition} from "./types";
import {hiddenButRequired} from "./designerModel";
import CCButton from "../Common/CCButton";
import css_buttons from "../Common/css/CCButton.module.scss";

/**
 * The properties of whatever is selected.
 *
 * WHAT IS OFFERED HERE IS EXACTLY WHAT THE DESIGNER OWNS. For a field the shape declares, that is
 * layout and wording — label, help, placeholder, width, read-only. It is deliberately NOT type, and
 * not required-ness: those come from the shape, the server overwrites them on every save, and
 * offering a control that is silently discarded is worse than offering none.
 *
 * An INVENTED field is the exception and says so, because its type is the one thing about it that
 * only this form knows.
 */

/** What each control is called on screen, and when it is the right choice. */
const CONTROL_LABELS: Record<string, string> = {
    Select: "Dropdown",
    MultiSelect: "Dropdown (multiple)",
    Checkboxes: "Checkboxes",
    Radio: "Radio buttons",
};

const CONTROL_HINTS: Record<string, string> = {
    MultiSelect: "Best for long lists, and for options that narrow as another field is answered.",
    Select: "Best for long lists, and for options that narrow as another field is answered.",
    Checkboxes: "Every choice visible at once. Best for a short, fixed list.",
    Radio: "Every choice visible at once. Best for a short, fixed list.",
};

type Props = {
    definition: FormDefinition;
    selection: Selection;
    /** The shape's fields, for the controls a placed field may be drawn with. */
    shapeFields?: ShapeField[];
    /** Options per field code, so a condition on a picker offers its values rather than raw keys. */
    fieldOptions?: Record<string, {Value: string; Display: string | null}[]>;
    disabled?: boolean;
    /** A RECORD's design only: the declared fields this record binds differently from its shape,
     *  so the owner can choose, per field, whether a save realigns it. */
    ownDefinitions?: OwnDefinition[];
    onPlacement: (code: string, patch: Partial<Placement>) => void;
    onDisplay: (index: number, patch: Partial<DisplayItem>) => void;
    onSection: (key: string, patch: Partial<Section>) => void;
    onTab: (key: string, patch: Partial<Tab>) => void;
    onRemoveTab: (key: string) => void;
};

export default function Inspector({definition, selection, disabled, shapeFields, fieldOptions, ownDefinitions,
                                   onPlacement, onDisplay, onSection, onTab, onRemoveTab}: Props) {
    return (
        <div style={{width: 300, flexShrink: 0, borderLeft: `1px solid ${Grey2}`, paddingLeft: 18,
                     overflowY: "auto"}}>
            <Typography style={{fontSize: 11, fontWeight: 700, color: Grey6, textTransform: "uppercase",
                                letterSpacing: "0.5px", marginBottom: 12}}>
                Properties
            </Typography>
            {body()}
        </div>
    );

    function body() {
        if (!selection) {
            return (
                <Typography style={{fontSize: 13, color: Grey7}}>
                    Select a question, a heading or a section to change how it is drawn.
                </Typography>
            );
        }

        if (selection.kind === "tab") {
            const tab = (definition.tabs ?? []).find(t => t.key === selection.key);
            if (!tab) return null;
            return (
                <Fields>
                    <TextField label="Title" size="small" disabled={disabled} value={tab.title ?? ""}
                               onChange={e => onTab(tab.key, {title: e.target.value})}/>
                    <Note>Key: {tab.key}</Note>
                    {/* Removing a tab costs the ARRANGEMENT and nothing else. Its sections stay,
                        outside the tabs, where they are always shown. */}
                    <CCButton className={css_buttons.whiteStandardButton} disabled={disabled}
                              onClick={() => onRemoveTab(tab.key)}>Remove tab</CCButton>
                    <Note>
                        Its sections stay on the form, outside the tabs. A tab holds an arrangement,
                        not questions.
                    </Note>
                </Fields>
            );
        }

        if (selection.kind === "section") {
            const section = (definition.sections ?? []).find(s => s.key === selection.key);
            if (!section) return null;
            return (
                <Fields>
                    <TextField label="Title" size="small" disabled={disabled} value={section.title ?? ""}
                               onChange={e => onSection(section.key, {title: e.target.value})}/>
                    <TextField label="Description" size="small" multiline minRows={2} disabled={disabled}
                               value={section.description ?? ""}
                               onChange={e => onSection(section.key, {description: e.target.value})}
                               helperText="Shown under the section title."/>
                    {(definition.tabs ?? []).length > 0 && (
                        <TextField select label="Tab" size="small" disabled={disabled}
                                   value={section.tab ?? ""}
                                   onChange={e => onSection(section.key,
                                                            {tab: e.target.value || null})}
                                   helperText="A section with no tab is always shown, above them.">
                            <MenuItem value="">No tab &mdash; always shown</MenuItem>
                            {(definition.tabs ?? []).map(t => (
                                <MenuItem key={t.key} value={t.key}>{t.title || t.key}</MenuItem>
                            ))}
                        </TextField>
                    )}
                    <Note>Key: {section.key}</Note>
                </Fields>
            );
        }

        if (selection.kind === "display") {
            const item = (definition.display ?? [])[selection.index];
            if (!item) return null;
            if (item.kind === "divider") {
                return <Note>A divider has nothing to configure.</Note>;
            }
            return (
                <Fields>
                    <TextField label={item.kind === "heading" ? "Heading" : "Instructions"}
                               size="small" multiline={item.kind === "instructions"}
                               minRows={item.kind === "instructions" ? 3 : undefined}
                               disabled={disabled} value={item.text ?? ""}
                               onChange={e => onDisplay(selection.index, {text: e.target.value})}/>
                </Fields>
            );
        }

        const placement = (definition.fields ?? []).find(f => f.code === selection.code);
        if (!placement) return null;
        const shapeField = (shapeFields ?? []).find(f => f.Code === placement.code);
        // WHICH CONTROL THIS FIELD IS ACTUALLY DRAWN WITH: the override when the form chose one,
        // the catalogue's derived control otherwise. Computed once rather than twice, because the
        // Control picker and the filter toggle both need the same answer and two derivations are
        // how they come to disagree.
        const effectiveControl = placement.control ?? shapeField?.ControlOptions?.[0] ?? "";
        const own = (ownDefinitions ?? []).find(d => d.Code === placement.code);

        return (
            <Fields>
                {/* THE OWNER'S CHOICE, PER FIELD (Ed, 2026-10-29). Shown only on a record's design and
                    only for a field this record binds differently from its shape. Ticked, a save keeps
                    the record's definition; unticked, the next save takes the shape's -- which is the
                    designer's rule and the way a record is brought back into line. */}
                {own && (
                    <div style={{padding: "8px 10px", border: "1px solid #E8C889", borderRadius: 4,
                                 backgroundColor: "rgba(201,130,10,0.06)"}}>
                        <Typography style={{fontSize: 12, fontWeight: 600, color: Grey17}}>Defined differently on this record</Typography>
                        <Typography style={{fontSize: 12, color: Grey6, marginTop: 2}}>
                            This record: {own.RecordType ?? "its own definition"}. The shape: {own.ShapeType ?? "the shape's"}.
                        </Typography>
                        <FormControlLabel
                            control={<Checkbox size="small" disabled={disabled}
                                               checked={!!placement.keepOwn}
                                               onChange={e => onPlacement(placement.code, {keepOwn: e.target.checked})}/>}
                            label={<span style={{fontSize: 13, color: Grey17}}>Keep this record&rsquo;s definition</span>}/>
                        <Typography style={{fontSize: 11, color: Grey6}}>
                            {placement.keepOwn
                                ? "A save keeps the definition and the answers that fit it."
                                : "The next save takes the shape's definition. Check the answers this field holds afterwards."}
                        </Typography>
                    </div>
                )}
                <TextField label="Label" size="small" disabled={disabled}
                           value={placement.label ?? ""}
                           onChange={e => onPlacement(placement.code, {label: e.target.value})}
                           helperText={`Defaults to "${placement.name ?? placement.code}".`}/>
                <TextField label="Help text" size="small" multiline minRows={2} disabled={disabled}
                           value={placement.help ?? ""}
                           onChange={e => onPlacement(placement.code, {help: e.target.value})}/>
                <TextField label="Placeholder" size="small" disabled={disabled}
                           value={placement.placeholder ?? ""}
                           onChange={e => onPlacement(placement.code, {placeholder: e.target.value})}/>
                {/* OFFERED ONLY WHERE THE TYPE ALLOWS ONE, and the list comes from the catalogue
                    rather than from here -- the designer, the save and the renderer read one list,
                    so an author cannot pick a control the server will refuse. */}
                {(shapeField?.ControlOptions?.length ?? 0) > 1 && (
                    <TextField select label="Control" size="small" disabled={disabled}
                               value={placement.control ?? shapeField!.ControlOptions![0]}
                               onChange={e => onPlacement(placement.code,
                                   {control: e.target.value === shapeField!.ControlOptions![0]
                                       ? null : e.target.value})}
                               helperText={CONTROL_HINTS[placement.control
                                   ?? shapeField!.ControlOptions![0]] ?? undefined}>
                        {shapeField!.ControlOptions!.map(c => (
                            <MenuItem key={c} value={c}>{CONTROL_LABELS[c] ?? c}</MenuItem>
                        ))}
                    </TextField>
                )}

                <TextField select label="Width" size="small" disabled={disabled}
                           value={placement.width ?? "full"}
                           onChange={e => onPlacement(placement.code, {width: e.target.value as any})}>
                    {WIDTHS.map(w => <MenuItem key={w} value={w}>{w}</MenuItem>)}
                </TextField>
                <FormControlLabel
                    control={<Checkbox size="small" disabled={disabled}
                                       checked={!!placement.readOnly}
                                       onChange={e => onPlacement(placement.code, {readOnly: e.target.checked})}/>}
                    label={<span style={{fontSize: 13, color: Grey17}}>Read-only</span>}/>

                {/* OFFERED AS A FILTER, in the list's Filter by pane beside Status and Type.
                    Counted under the reader's own persona, so a number here can never promise a
                    record they cannot open.

                    SHOWN ONLY WHERE IT CAN BE TICKED. A facet is the values held with a count
                    against each: useful for five departments, useless for free text, where the
                    list is as long as the record count and every count is one. Search already
                    answers that question better. Offering the checkbox on a Text field and then
                    refusing the save would be a worse way to say the same thing -- so the rule is
                    drawn here and enforced on the server, the same split as Control above. */}
                {FILTERABLE_CONTROLS.includes(effectiveControl) ? (
                    <>
                        <FormControlLabel
                            control={<Checkbox size="small" disabled={disabled}
                                               checked={!!placement.filterable}
                                               onChange={e => onPlacement(placement.code, {filterable: e.target.checked})}/>}
                            label={<span style={{fontSize: 13, color: Grey17}}>Offer as a filter</span>}/>
                        {/* WHICH FILTER, SAID PLAINLY. The box reads the same on both kinds but
                            does two different things, and an author who ticks it on a Number
                            expecting a list of every quantity ever entered has been misled by a
                            label rather than told anything. */}
                        {placement.filterable ? (
                            <Typography sx={{fontSize: 11, color: Grey7, mt: "-4px", mb: "4px", ml: "32px"}}>
                                {RANGE_FACET_CONTROLS.includes(effectiveControl)
                                    ? "A range: two bounds, shown with the span the answers cover."
                                    : "A list of the answers given, with a count against each."}
                            </Typography>
                        ) : null}
                    </>
                ) : null}

                {/* WHEN TO SHOW IT. One condition, which covers the case this exists for: a text
                    field that appears when a picker includes "Other". Presentation only -- the note
                    below says so, because a hidden field is still required if its rule says so. */}
                <ShowWhenEditor
                    placement={placement}
                    definition={definition}
                    disabled={disabled}
                    fieldOptions={fieldOptions}
                    onChange={next => onPlacement(placement.code, {showWhen: next})}/>

                <Note>Code: {placement.code}</Note>
                {placement.new ? (
                    <Note>
                        This question is not on the shape. Releasing the form adds it, on a draft of the
                        shape that somebody then releases. Until then nothing catalogues it.
                    </Note>
                ) : (
                    // Said rather than left as an absence, so nobody hunts for a type control that
                    // was deliberately not offered.
                    <Note>
                        Its type and when it is required come from the shape, and are not changed here.
                    </Note>
                )}
            </Fields>
        );
    }
}

function Fields({children}: {children: React.ReactNode}) {
    return <div style={{display: "flex", flexDirection: "column", gap: 14}}>{children}</div>;
}

function Note({children}: {children: React.ReactNode}) {
    return <Typography style={{fontSize: 11, color: Grey6, lineHeight: 1.5}}>{children}</Typography>;
}


/**
 * "Show this only when <field> <is> <value>."
 *
 * ONE CONDITION, not a rule builder. The case this exists for is a field that appears when a picker
 * includes "Other", and a screen that can express that well is worth more than one that can express
 * everything badly. The stored shape is a LIST, so more conditions can be authored later without the
 * documents written today needing to change.
 *
 * THE FIELDS OFFERED ARE THE ONES ON THE FORM, minus this one -- a field cannot depend on itself, it
 * would have to be visible to be answered and answered to be visible, and the server refuses it.
 */
function ShowWhenEditor({placement, definition, disabled, fieldOptions, onChange}: {
    placement: Placement;
    definition: FormDefinition;
    disabled?: boolean;
    fieldOptions?: Record<string, {Value: string; Display: string | null}[]>;
    onChange: (next: Condition[] | null) => void;
}) {
    const condition: Condition | null = Array.isArray(placement.showWhen) && placement.showWhen.length
        ? placement.showWhen[0] : null;
    const atRisk = hiddenButRequired(placement);

    const candidates = (definition.fields ?? []).filter(f => f.code !== placement.code);
    const options = condition ? (fieldOptions?.[condition.field] ?? []) : [];
    const takesValue = condition ? OPERATORS_TAKING_A_VALUE.has(condition.operator) : false;

    const set = (patch: Partial<Condition>) => {
        const next = {...(condition ?? {field: "", operator: "EQUAL" as const}), ...patch};
        onChange(next.field ? [next] : null);
    };

    return (
        <div style={{display: "flex", flexDirection: "column", gap: 10,
                     borderTop: `1px solid ${Grey2}`, paddingTop: 14}}>
            <Typography style={{fontSize: 11, fontWeight: 700, color: Grey6,
                                textTransform: "uppercase", letterSpacing: "0.5px"}}>
                Show only when
            </Typography>

            <TextField select size="small" label="Field" disabled={disabled}
                       value={condition?.field ?? ""}
                       onChange={e => e.target.value ? set({field: e.target.value}) : onChange(null)}>
                <MenuItem value="">Always show</MenuItem>
                {candidates.map(f => (
                    <MenuItem key={f.code} value={f.code}>{f.label || f.name || f.code}</MenuItem>
                ))}
            </TextField>

            {condition && (
                <TextField select size="small" label="Is" disabled={disabled}
                           value={condition.operator}
                           onChange={e => set({operator: e.target.value as Condition["operator"]})}>
                    {SHOW_WHEN_OPERATORS.map(o => (
                        <MenuItem key={o} value={o}>{o.toLowerCase()}</MenuItem>
                    ))}
                </TextField>
            )}

            {condition && takesValue && (
                options.length ? (
                    <TextField select size="small" label="Value" disabled={disabled}
                               value={Array.isArray(condition.value)
                                   ? (condition.value[0] ?? "") : (condition.value ?? "")}
                               onChange={e => set({value: condition.operator === "IN"
                                   ? [e.target.value] : e.target.value})}>
                        {options.map(o => (
                            <MenuItem key={o.Value} value={o.Value}>{o.Display ?? o.Value}</MenuItem>
                        ))}
                    </TextField>
                ) : (
                    <TextField size="small" label="Value" disabled={disabled}
                               value={Array.isArray(condition.value)
                                   ? (condition.value[0] ?? "") : (condition.value ?? "")}
                               onChange={e => set({value: condition.operator === "IN"
                                   ? [e.target.value] : e.target.value})}/>
                )
            )}

            {condition && !atRisk && (
                <Note>
                    Presentation only. If this field is required it stays required even while hidden,
                    and the save will refuse without it.
                </Note>
            )}

            {/* THE CASE WORTH INTERRUPTING FOR. The server behaves correctly -- it refuses the save
                and names the field -- but it names a field nobody can see, and the person who can
                fix it is here, now. */}
            {atRisk && (
                <div style={{border: "1px solid #B26500", borderRadius: 3, padding: "8px 10px",
                             background: "rgba(178,101,0,0.06)"}}>
                    <Typography style={{fontSize: 12, color: "#B26500", lineHeight: 1.5}}>
                        <b>This can be required while hidden.</b> When that happens the save is
                        refused for a question nobody can see. Show it whenever it is required, or
                        make it optional.
                    </Typography>
                </div>
            )}
        </div>
    );
}
